import { backButton } from '@tma.js/sdk-react'
import { useEffect } from 'react'
import { useLatest } from '@/shared/lib'

type Handler = () => void

/**
 * Системная кнопка «Назад» — одна на приложение, а желающих управлять ею двое.
 *
 * Оболочка задаёт поведение по маршруту (`fallback`: назад по истории, на
 * корне кнопки нет). Экран может его перехватить (`override`) — так экран
 * оплаты спрашивает подтверждение вместо разрыва транзакции. Уровни разные
 * намеренно: эффекты дочерних компонентов срабатывают раньше родительских, и
 * при одном общем стеке оболочка перекрывала бы экран.
 */
let fallback: Handler | null = null
/** Перехваты экранов; `null` — экран прячет кнопку (экраны-моменты 09, 10, 14). */
const overrides: Array<{ handler: Handler | null }> = []

const current = (): Handler | null => {
  // Не `.at(-1)`: его нет в WebView старше Chrome 92 / iOS 15.4.
  const top = overrides[overrides.length - 1]

  return top ? top.handler : fallback
}

const sync = (): void => {
  if (current()) {
    backButton.show.ifAvailable()
  } else {
    backButton.hide.ifAvailable()
  }
}

/** Подписка на нажатие — один раз при инициализации SDK. */
export const bindBackButton = (): void => {
  backButton.onClick.ifAvailable(() => current()?.())
}

/** Поведение по маршруту. Только для оболочки приложения; `null` — кнопки нет. */
export const useBackButtonFallback = (handler: Handler | null): void => {
  const latest = useLatest(handler)
  const enabled = handler !== null

  useEffect(() => {
    fallback = enabled ? () => latest.current?.() : null
    sync()

    return () => {
      fallback = null
      sync()
    }
  }, [enabled, latest])
}

/** Перехват кнопки экраном, пока он смонтирован. */
export const useBackButton = (handler: Handler | null): void => {
  const latest = useLatest(handler)
  const enabled = handler !== null

  useEffect(() => {
    if (!enabled) {
      return
    }

    const entry = { handler: () => latest.current?.() }

    overrides.push(entry)
    sync()

    return () => {
      overrides.splice(overrides.indexOf(entry), 1)
      sync()
    }
  }, [enabled, latest])
}

/**
 * Спрятать кнопку, пока экран смонтирован (`enabled`): экраны-моменты без
 * «Назад» — открытие подарка, решение, «Подарок возвращён» (CLAUDE.md,
 * «Навигация „Назад“»). Перехват экрана поверх этого (`useBackButton` в
 * дочернем компоненте) снова покажет кнопку.
 */
export const useHideBackButton = (enabled = true): void => {
  useEffect(() => {
    if (!enabled) {
      return
    }

    const entry = { handler: null }

    overrides.push(entry)
    sync()

    return () => {
      overrides.splice(overrides.indexOf(entry), 1)
      sync()
    }
  }, [enabled])
}
